import Hls from 'hls.js';

const noop = () => {};
const NOT_ALLOWED_ERROR = 'NotAllowedError';
/**
 * Handle video element and incoming hlsjs events and map them to corresponding DFV actions.
 */
class VideoEventBus {
    constructor ({ video, stream, syncController, setStreamError, setIsLoading, setAutoPlayError }) {
        this.video = video;
        this.stream = stream;
        this.isPlaying = false;
        this.syncController = syncController;
        this.setStreamError = setStreamError;
        this.setIsLoading = setIsLoading;
        this.hls = null;
        this.canAutoPlay = null;
        this.debounceId = -1;
        this.setAutoPlayError = setAutoPlayError;
        this.initAutoPlayResolver();
        this.segmentsRequested = false;
    }

    types = Object.freeze(['play', 'playing', 'pause', 'complete', 'ended', 'loadeddata', 'canplay', 'error', 'start', 'stop', 'init', 'click', Hls.Events.MEDIA_ATTACHED, Hls.Events.ERROR, Hls.Events.LEVEL_LOADED]);

    bind () {
        if (this.video) {
            this.types.forEach(this.bindHelper, this);
        }
    }

    bindHelper (type) {
        this.video.addEventListener(type, this);
    }

    unBindHelper (type) {
        this.video.removeEventListener(type, this);
    }

    initializeHls () {
        if (!this.hls) {
            this.hls = new Hls();
            [Hls.Events.MEDIA_ATTACHED, Hls.Events.ERROR, Hls.Events.LEVEL_LOADED].forEach((e) => {
                this.hls.on(e, (type, data) => {
                    // Clone incoming event from hlsjs
                    const repeatEvent = new Event(type);
                    repeatEvent.data = data;
                    this.handleEvent(repeatEvent);
                });
            });
        }
    }

    initAutoPlayResolver () {
        // Low budget Promise.withResolvers()
        // but with a single use catch.
        const autoPlayResolver = {};
        autoPlayResolver.promise = new Promise((resolve, reject) => {
            autoPlayResolver.resolve = resolve;
            autoPlayResolver.reject = reject;
        }).catch((err) => {
            autoPlayResolver.reject = noop;
            this.canAutoPlay = false;
            console.error(err);
            if (err.name === NOT_ALLOWED_ERROR) {
                this.setIsLoading(false);
                this.setAutoPlayError(true);
            }
            autoPlayResolver.promise = new Promise((resolve, reject) => {
                autoPlayResolver.resolve = resolve;
            });
            return autoPlayResolver.promise;
        }).then(() => {
            autoPlayResolver.resolve = noop;
            this.canAutoPlay = true;
            this.setIsLoading(false);
        });

        this.autoPlayResolver = autoPlayResolver;
    }

    play () {
        const { video, autoPlayResolver, canAutoPlay } = this;
        if (video) {
            // If we haven't established autoplay capabilities, lets figure it out.
            if (canAutoPlay == null) {
                Promise.resolve(video.play()).then(autoPlayResolver.resolve, autoPlayResolver.reject);
            } if (canAutoPlay) {
                // We've confirmed we can auto play, so just do it.
                video.play();
            } else {
                // If we haven't successfully auto-played up until this point
                // user interaction may revive the player, however we'll need
                // to attempt play again manually.
                // Debounce attempts to 2s to not overwhelm
                this.debounceId = setTimeout(() => {
                    if (video && video.parentNode && autoPlayResolver) {
                        Promise.resolve(video.play()).then(autoPlayResolver.resolve, autoPlayResolver.reject);
                    }
                }, 2000);
            };
        }
    }

    // Primary event switchboard.
    handleEvent (e) {
        const video = this.video;
        // In a bad state... video is being cleaned up or crashed.
        if (!video || !video.parentNode) {
            return;
        }
        switch (e.type) {
            case 'play':
            case 'playing':
                this.isPlaying = true;
                // If we recover playback either by debounced user interaction or specific click to play, remove autoplay error state.
                this.setAutoPlayError(false);
                break;
            case 'pause':
            case 'complete':
            case 'ended':
                if (this.isPlaying) {
                    this.isPlaying = false;
                    this.play();
                }
                break;
            case 'loadeddata':
                // done loading
                this.handleLoaded();
                break;
            case 'canplay':
            case 'click':
                if (!this.isPlaying || video.paused || video.stopped) {
                    this.play();
                }
                break;
            case Hls.Events.MEDIA_ATTACHED:
                console.log('MEDIA ATTACHED');
                break;
            case Hls.Events.LEVEL_LOADED:
                if (!this.segmentsRequested) {
                    const { fragments } = e.data?.details;
                    if (fragments?.length > 0) {
                        this.syncController?.current.seekToTimeStamp(fragments);
                        this.segmentsRequested = true;
                    }
                }
                break;
            case Hls.Events.ERROR:
            case 'error':
                if (e.data) {
                    // Trigger upstream failsafe to force an error view
                    // in the case that the stream has failed and cannot recover.
                    // https://github.com/video-dev/hls.js/blob/v1.4.12/docs/API.md#fifth-step-error-handling
                    if (e.data.fatal) {
                        this.setStreamError(true);
                    } else {
                        this.handleHlsEvent('error-recovery');
                    }
                }
                break;
            case 'init':
                this.handleHlsEvent('init');
                break;
            case 'start':
                this.handleHlsEvent('start');
                break;
            case 'stop':
                this.handleHlsEvent('stop');
                break;
        }
    }

    handleHlsEvent (passedType) {
        const hls = this.hls;
        const stream = this.stream;
        const video = this.video;
        let self;

        switch (passedType) {
            case 'init':
                self = this;
                self.initializeHls();
                self.hls.attachMedia(video);
                self.hls.on(Hls.Events.MEDIA_ATTACHED, () => {
                    self.hls.loadSource(stream);
                });
                break;
            case 'start':
                hls && hls.startLoad();
                break;
            case 'stop':
                hls && hls.stopLoad();
                break;
            case 'error-recovery':
                hls && hls.recoverMediaError();
                break;
        }
    }

    setStreamError () {
        // to be implemented/overridden via constructor.
    }

    setIsLoading () {
        // to be implemented/overridden via constructor.
    }

    setAutoPlayError () {
        // to be implemented/overridden via constructor.
    }

    handleLoaded () {
        const syncController = this.syncController?.current;
        const canManage = !!syncController?.manageStream;

        if (canManage) {
            syncController.manageStream();
        }

        this.setIsLoading(false);
    }

    destroy () {
        const video = this.video;
        if (video) {
            this.types.forEach(this.unBindHelper, this);
        }
        this.video = null;
        this.setStreamError = null;
        this.setIsLoading = null;
        this.setAutoPlayError = null;
        this.syncController = null;
        this.autoPlayResolver = null;
        clearTimeout(this.debounceId);

        if (this.hls) {
            this.hls.destroy();
            this.hls = null;
        }
    }
}

export default VideoEventBus;
